import { FunctionComponent, useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

import { Moment } from 'moment-timezone';
import AddOrEditComplementaryItem from './AddOrEditComplementaryItem';
import CreateBookingHeader from '../CreateBookingHeader';
import { AddOnDate, Discount, itemTypes, versionPetAndServices } from 'model/NewMultipleBooking';
import { Product } from '@spike/product-model';
import AddOnService from '@spike/addon-service-model';
import { useDispatch } from 'react-redux';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import { useApiClientWrapper } from 'hooks';

interface Props {
    type?: itemTypes;
    parentId: number;
    petId?: number;
    date?: Moment;
    versionPetAndServices?: versionPetAndServices;
    product?: Product;
    addOn?: AddOnService;
    custom?: string;
    discount?: Discount;
    quantity?: number;
    amount?: number;
    taxId?: number;
    staffId?: number;
    itemsUsed?: Array<number>;
    loading?: boolean;
    petWithoutSelect?: boolean;
    dates?: Array<AddOnDate>;
    primaryServiceStaffId?: number;
    petBusinessAreas?: Array<string>;
    onChangeDate?: (value: Moment) => void;
    onBack: () => void;
    onBook: (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string,
        staffId?: number,
        discount?: Discount,
        quantity?: number,
        amount?: number,
        taxId?: number,
        dates?: Array<Moment>
    ) => void;
    onRemove?: (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string
    ) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%'
        },
        petContainer: {
            marginTop: 8,
            marginBottom: 8
        },
        headerContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            margin: '17px 27px 11px 27px',
            [theme.breakpoints.up('md')]: {
                margin: '20px 30px 13px 30px'
            }
        },
        content: {
            height: '91%'
        },
        addService: {
            cursor: 'pointer',
            color: '#92B4A7',
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '27px',
            paddingTop: 7
        }
    })
);

export const ComplementaryItem: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const [date, setDate] = useState<Moment | undefined>(props.date);
    const [changedDate, setChangedDate] = useState<boolean>(false);

    const getTitle = () => {
        const prefix = props.versionPetAndServices === versionPetAndServices.add ? 'Add' : 'Edit';
        const items = {
            [itemTypes.service]: '',
            'add-on': `${prefix} Add-On`,
            [itemTypes.product]: `${prefix} Product`,
            [itemTypes.custom]: `${prefix} Custom Item`
        };
        return items[props.type as itemTypes] || '';
    };

    const handlerBack = () => {
        setDate(props.date);
        props.onBack();
    };

    const handlerChangeDate = (dateSelected: Moment) => {
        setDate(dateSelected);
        if (date?.format('L') !== dateSelected.format('L')) {
            setChangedDate(true);
        }
        props.onChangeDate && props.onChangeDate(dateSelected);
    };

    useEffect(() => {
        dispatch(fetchStaffThunk(apiClientWrapper.marketplaceId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiClientWrapper.marketplaceId]);

    const addView = (
        <AddOrEditComplementaryItem
            loading={props.loading}
            parentId={props.parentId!}
            petId={props.petId}
            versionPetAndServices={props.versionPetAndServices}
            date={date}
            type={props.type}
            taxId={props.taxId}
            itemsUsed={props.itemsUsed}
            petWithoutSelect={props.petWithoutSelect || false}
            dates={props.dates}
            primaryServiceStaffId={props.primaryServiceStaffId}
            petBusinessAreas={props.petBusinessAreas}
            changedDate={changedDate}
            onChangeDate={handlerChangeDate}
            onBook={props.onBook}
            onRemove={props.onRemove}
        />
    );

    const editView = (
        <AddOrEditComplementaryItem
            loading={props.loading}
            parentId={props.parentId!}
            petId={props.petId}
            versionPetAndServices={props.versionPetAndServices}
            date={date}
            product={props.product}
            addOn={props.addOn}
            custom={props.custom}
            staffId={props.staffId}
            quantity={props.quantity}
            amount={props.amount}
            taxId={props.taxId}
            discount={props.discount}
            itemsUsed={props.itemsUsed}
            type={props.type}
            petWithoutSelect={props.petWithoutSelect || false}
            dates={props.dates}
            primaryServiceStaffId={props.primaryServiceStaffId}
            petBusinessAreas={props.petBusinessAreas}
            changedDate={changedDate}
            onChangeDate={handlerChangeDate}
            onBook={props.onBook}
            onRemove={props.onRemove}
        />
    );

    return (
        <Box className={classes.container}>
            <Box className={classes.headerContainer}>
                <CreateBookingHeader
                    title={getTitle()}
                    onBack={handlerBack}
                />
            </Box>
            <Box className={classes.content}>
                {props.versionPetAndServices === versionPetAndServices.add ? addView : editView}
            </Box>
        </Box>
    );
};

export default ComplementaryItem;
